import React, { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {IProps, IState} from './types';
import * as S from './styles';
import { useHistory } from 'react-router';
import {boxesIntersect, SelectionBox} from "react-drag-to-select";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import PreviewComponent from '@routes/private/Studio/components/PreviewComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loading from '@components/Loading';
import EmptyDesignIcon from '@assets/icons/emptyDesignIcon';
import Button from '@components/Button';
import { DesignsContext } from '@utils/DOIgnition/DesignsContext';
import selectors from '@state/selectors';
import MouseSelection from '@components/MouseSelection';
import RightClickMenu from '../RightClickMenu';

const DesignsList:React.FC<IProps> = (props, state: IState) => {
  const{ searchSelectedAll, searchSelectedOne, searchValue, allFilters, sortByText} = useContext(DesignsContext);
  const history: any = useHistory();
  const counter = useSelector(selectors.designs.getCounterDesigns);

  const [openOptions, setOpenOptions] = useState(false);
  const [coordinates, setCoordinates] = useState({x: 0, y: 0});
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [allowClean, setAllowClean] = useState<boolean>(true);
  let selectedRefs = React.useRef([] as any);
  const stateRef = useRef<boolean>();

  const handleSetCoordinates = (e: React.MouseEvent<HTMLDivElement>) => {
    if (openOptions === false && !props.blockSelection) setCoordinates({x: e.clientX, y: e.clientY});
  }

  useEffect(() => {
    if (stateRef) stateRef.current = allowClean;
  }, [allowClean]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [])

  const handleClickOutside = (e: any)=> {
    if(selectedRefs && selectedRefs.current){
        const ref: any = selectedRefs.current
        let found = false;
        if (ref) {
          ref.forEach(function (refDesign: any) {
            if (refDesign?.contains(e.target)) found= true;
          });

          setTimeout(() => {
            if (!found && stateRef.current) {
              if (props.onActions && !e.shiftKey){
                props.onActions([], 'SELECTEDDESIGNS');
                setSelectedIndexes([]);
                setOpenOptions(false);
              }
              
            }
          }, 200)
        }
    }
  }
  
  useEffect(() => {
    if (props.currentDesigns) setSelectedIndexes([]);
  }, [props.currentDesigns])

  const handleSelectionStart = useCallback(
    () => {
      setAllowClean(false);
    },
    []
  );

  const handleSelectionEnd = useCallback(
    (box: SelectionBox) => {
      let indexesToSelect = [] as any;
      let designsToSelect = [] as any;
      const elementsContainer = document.getElementById("design-list");
      if (elementsContainer) {
        Array.from(elementsContainer.childNodes).forEach((item, index) => {
          const boundingBox = (item as Element).getBoundingClientRect();
          if (boxesIntersect(box, boundingBox)) {
            indexesToSelect.push(index);
            if (props.currentDesigns) designsToSelect.push(props.currentDesigns[index]);
          }
        });
      }
      
      setSelectedIndexes(indexesToSelect);
      if (props.onActions) props.onActions(designsToSelect, 'SELECTEDDESIGNS');
    },
    [props.currentDesigns]
  );

  const handleSelectionChange = useCallback((box) => {
  }, []);

  const renderEndMessage = () => {
    return(
      <S.EndContainer>
      </S.EndContainer>
    )
  }

  const renderLoaderContainer = () => {
    if (searchSelectedOne) return '';
    else { 
      return(
        <S.LoaderContainer>
          <Loading 
            id={"loading-designs"}
            containerStyle={{justifyContent: 'center'}}
          />
        </S.LoaderContainer>
      );
    }
  }

  const handleCreateNewDesign = () => {
    history.push({pathname: '/studio'});
  }

  const updateSelectedDesigns = (design: any, add: boolean) => {
    let index =props.currentDesigns.findIndex( (item:any) => item.id === design.id);
    if (index!==-1) {
      if (!add) setSelectedIndexes(selectedIndexes.filter(item => item!==index));
      else setSelectedIndexes([...selectedIndexes, index]);
    }
  }

  const onActions = (item: any, action: string, folder?:number) => {
    if (action==="ADDDESIGN") updateSelectedDesigns(item, true);
    if (action==="DELETEDESIGN") updateSelectedDesigns(item, false);

    if (folder && props.onActions) props.onActions(item,action, folder);
    else if (props.onActions) props.onActions(item, action);
  }
  
  return ( 
    <S.Root
      id="infinite-scroll-all-designs"
      onMouseMove={handleSetCoordinates}
    >
      {
        props.isLoading ?
        <S.AllLoaderContainer>
          <Loading 
            id={"loading-designs"}
            containerStyle={{justifyContent: 'center'}}
          />
        </S.AllLoaderContainer>
        : props.hasNoDesigns ?
          <S.HasNoDesignsContainer>
            <S.EmptyIcon>
              <S.EmptyIconScale>
                <EmptyDesignIcon/>
              </S.EmptyIconScale>
            </S.EmptyIcon>
            <S.EmptyTextContainer>
              <S.EmptyTitle>
                {props.blockSelection ? 'No designs shared yet' :'No designs added yet'}
              </S.EmptyTitle>
              {!props.blockSelection &&
              <S.EmptySubtitle>
                Go to your Studio Dashboard and start creating designs. You’ll be able to keep them organized here.
              </S.EmptySubtitle>}
              <S.EmptyButton>
                <Button
                  icon="add" 
                  label="CREATE NEW"
                  style={{hoverBackgroundColor: "#344a70", backgroundColor: "#19325d", width: 153, height: 36, boxSizing: 'border-box'}}
                  labelStyle={{color: "white", fontWeight: '500'}}
                  onClick={handleCreateNewDesign}
                />
              </S.EmptyButton>
            </S.EmptyTextContainer>
          </S.HasNoDesignsContainer>
        :
          <S.DesignsContainer onMouseDown={()=>setAllowClean(true)}>
            {!props.blockSelection && <MouseSelection onSelectionChange={handleSelectionChange} onSelectionEnd={handleSelectionEnd} onSelectionStart={handleSelectionStart}/>}
            {(searchSelectedAll || allFilters.length>0) ?
            <S.Result>
              Results {searchValue!=='' && `for “${searchValue}”`}
              {counter!=null && 
              <S.Counter>{ new Intl.NumberFormat("en-IN").format(counter.counter)} results based on your criteria</S.Counter>}
              {sortByText!=''&&<S.Orderby>Sorted: {sortByText}</S.Orderby>}
            </S.Result> 
            :
              <S.Result hide={sortByText===''}>
                {sortByText!=''&&<S.Orderby>Sorted: {sortByText}</S.Orderby>}
              </S.Result> 
            }
            
            <InfiniteScroll
              dataLength={props.currentDesigns ? props.currentDesigns.length : 0}
              next={() => props.loadMoreDesigns(undefined)}
              hasMore={props.hasMore}
              loader={renderLoaderContainer()}
              scrollableTarget="infinite-scroll-all-designs"
              endMessage={renderEndMessage()}
              style={{
                width: '100%',
                minHeight: '400px',
                overflowX: 'hidden',
              }}
            >
              <S.DesignsWrap flex={props.currentDesigns && props.currentDesigns.length > 0}>
                <TransitionGroup className="group-list" id="design-list">
                  {
                    props.currentDesigns && props.currentDesigns.length > 0 &&
                    props.currentDesigns.map((design: any, index: number) => {
                      return (
                        <CSSTransition key={design.id} timeout={600} classNames="item">
                          <div ref={selectedIndexes.includes(index) ? ref=>selectedRefs.current.push(ref) : null}>
                          <PreviewComponent
                            index={index}
                            key={index}
                            item={design}
                            showMetadata={true}
                            onActions={onActions}
                            selected={selectedIndexes.includes(index) }
                            setOpen={setOpenOptions}
                          />
                          </div>
                        </CSSTransition>
                      )
                    })
                  }
                </TransitionGroup>
                {
                  props.hasMore === false && props.currentDesigns?.length === 0 &&
                  <S.TabNoDesignsContainer>
                    <S.EmptyIcon>
                      <S.EmptyIconScale>
                        <EmptyDesignIcon/>
                      </S.EmptyIconScale>
                    </S.EmptyIcon>
                    <S.EmptyTextContainer>
                      <S.EmptyTitle>
                        {props.blockSelection ? 'No designs shared yet' :'No designs added yet'}
                      </S.EmptyTitle>
                      {!props.blockSelection && 
                      <S.TabEmptySubtitle>
                        Go to all your designs and start moving designs to this folder
                      </S.TabEmptySubtitle>}
                    </S.EmptyTextContainer>
                  </S.TabNoDesignsContainer>
                }
              </S.DesignsWrap>
            </InfiniteScroll>
            <RightClickMenu
              open={openOptions}
              setOpen={setOpenOptions}
              coordinates={coordinates}
              section={undefined}
              selectedIndexes={selectedIndexes}
              items={props.currentDesigns}
              onActions={props.onActions}
              selectedFolder={props.selectedFolder}
              handleWarningScreen={props.handleWarningScreen}
            />
          </S.DesignsContainer>
      }
    </S.Root>
  );
}

export default DesignsList;